import { StationaryBookingExistsBanner } from '@components/BookingFlow/Stationary/StationaryBookingExistsBanner';
import { CustomerLoginModal } from '@components/Login/CustomerLoginModal';
import { useCustomerDataQualityGate } from '@components/UserGroupDataQuality/useCustomerDataQualityCheckout';
import { t } from '@lingui/core/macro';
import { PaymentOption } from '@navio/nsdk';
import { invalidateQuery, URLKeys } from 'noddi-async';
import { FieldErrorMessage, NoddiButton, noddiToast } from 'noddi-ui-web';
import { useAuthContext, type BookingOrderMoney } from 'noddi-web';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { useCreateStationaryBookingWithPayment } from '../../../hooks/useCreateStationaryBookingWithPayment';
import {
  getConfirmCtaText,
  getInvoiceReferenceBlocker,
  getStationaryConfirmClickBlocker,
  shouldPayBeforeOnConfirm,
  type InvoiceReferenceInput,
  type StationaryCreateBodyResult
} from '../../../utils/stationaryConfirm';
import { ApiErrorMessageWithTrans } from '../../Error/ApiErrorMessageWithTrans';

export type { InvoiceReferenceInput };

type Props = {
  paymentOption: PaymentOption;
  totalAmount: number;
  /** Set only where checkout asks for a reference; omitted, none is sent. */
  invoiceReference?: InvoiceReferenceInput;
  hasValidPhone: boolean;
  hasOrganizationNumber?: boolean;
  /** Parent owns expiry; when true CTA is disabled and click shows re-pick message. */
  isReservationExpired?: boolean;
  disabled?: boolean;
  /** Where click-validation errors go: toast = booking flow; inline = proposal (under button). API errors always render under the button. */
  errorMode?: 'toast' | 'inline';
  allowLoginPrompt?: boolean;
  onBookingCreated?: (data: {
    slug: string;
    paymentMethod: PaymentOption;
    bookingId?: number;
    order?: BookingOrderMoney | null;
  }) => void;
  buildCreateBody: () => StationaryCreateBodyResult;
  aboveButton?: ReactNode;
};

/** Shared stationary confirm + pay CTA (booking flow and proposal). */
export const ConfirmStationaryBookingButton = ({
  paymentOption,
  totalAmount,
  invoiceReference,
  hasValidPhone,
  hasOrganizationNumber = true,
  isReservationExpired = false,
  disabled = false,
  errorMode = 'toast',
  allowLoginPrompt = false,
  onBookingCreated,
  buildCreateBody,
  aboveButton
}: Props) => {
  const { isLoggedIn, tokenLoginOfUsers } = useAuthContext();
  const [validationError, setValidationError] = useState<string | null>(null);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const shouldPayBefore = shouldPayBeforeOnConfirm(paymentOption, totalAmount);
  // Covers the whole click, including post-create navigation — mutation isPending
  // alone leaves the CTA clickable in the gaps and lets a second click create a
  // second booking.
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showError = (message: string) => {
    if (errorMode === 'inline') {
      setValidationError(message);
      return;
    }
    noddiToast.error(message);
  };

  const { createStationaryBooking, isPending, createBookingError, isBookingExistsError, existingBookingUrl } =
    useCreateStationaryBookingWithPayment({
      onBookingCreated
    });

  const isBusy = isSubmitting || isPending;

  const refreshAuthAndQueries = useCallback(async () => {
    if (!isLoggedIn) {
      return;
    }

    await tokenLoginOfUsers();
    await invalidateQuery({ urlKey: URLKeys.getBookingProposalBySlug });
    await invalidateQuery({ urlKey: URLKeys.getOrganizationsForCar });
  }, [isLoggedIn, tokenLoginOfUsers]);

  const refreshInFlight = useRef(false);
  useEffect(() => {
    const refreshIfActive = () => {
      if (document.visibilityState !== 'visible' || refreshInFlight.current || !isLoggedIn) {
        return;
      }

      refreshInFlight.current = true;
      void refreshAuthAndQueries()
        .then(() => setRefreshError(null))
        .catch(() => setRefreshError(t`We could not refresh your account. Check your connection and try again.`))
        .finally(() => {
          refreshInFlight.current = false;
        });
    };

    window.addEventListener('focus', refreshIfActive);
    document.addEventListener('visibilitychange', refreshIfActive);
    return () => {
      window.removeEventListener('focus', refreshIfActive);
      document.removeEventListener('visibilitychange', refreshIfActive);
    };
  }, [isLoggedIn, refreshAuthAndQueries]);

  const createBooking = async () => {
    // No pre-flight refresh here: the create body is built entirely from the booking
    // draft plus props, and the focus/visibilitychange listener above already refreshes
    // membership when the user returns to this tab. Awaiting a token-login plus two
    // refetches only added round-trips before the POST.
    const invoiceReferenceBlocker = getInvoiceReferenceBlocker(invoiceReference);
    if (invoiceReferenceBlocker) {
      showError(invoiceReferenceBlocker);
      return;
    }
    const result = buildCreateBody();
    if (!result.ok) {
      showError(result.message);
      return;
    }

    setIsSubmitting(true);

    try {
      await createStationaryBooking(
        {
          ...result.body,
          payment: {
            paymentMethod: paymentOption,
            ...(invoiceReference && { invoiceReference: invoiceReference.value.trim() })
          }
        },
        {
          shouldPayBefore,
          paymentMethod: paymentOption
        }
      );
    } catch {
      // Mutation error state drives exists-banner / inline API error UI.
    } finally {
      setIsSubmitting(false);
    }
  };

  const dataQuality = useCustomerDataQualityGate();

  const handleClick = async () => {
    setValidationError(null);

    if (!isLoggedIn) {
      if (allowLoginPrompt) {
        setShowLoginModal(true);
      }
      return;
    }

    // The report is the preferred gate: where it can fix a detail, the click opens
    // the sheet instead of hitting the dead-end messages below. The local blockers
    // stay for brands whose data-quality prompt is off, where the report is always valid.
    if (
      dataQuality.holdForDataQuality({
        willPay: shouldPayBefore,
        onSaved: () => {
          void createBooking();
        }
      }) !== 'clear'
    ) {
      return;
    }

    const blockMessage = getStationaryConfirmClickBlocker({
      isLoggedIn,
      isReservationExpired,
      hasValidPhone,
      hasOrganizationNumber
    });
    if (blockMessage) {
      showError(blockMessage);
      return;
    }

    await createBooking();
  };

  const loggedInCta = getConfirmCtaText(paymentOption, shouldPayBefore);
  const ctaText =
    !isLoggedIn && allowLoginPrompt
      ? shouldPayBefore
        ? t`Log in to confirm and pay`
        : t`Log in to confirm booking`
      : loggedInCta;

  const isDisabled =
    isBusy ||
    isReservationExpired ||
    disabled ||
    (!allowLoginPrompt && !isLoggedIn) ||
    // A bad phone disables the CTA only when nothing can fix it here; otherwise the
    // click has to reach holdForDataQuality and open the sheet.
    (isLoggedIn && !hasValidPhone && !dataQuality.canFix) ||
    dataQuality.isChecking;

  return (
    <div className='flex w-full flex-col gap-2'>
      {allowLoginPrompt && (
        <CustomerLoginModal
          open={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          title={t`Sign in to confirm booking`}
          description={t`Please sign in with your phone number to complete the booking`}
        />
      )}
      {aboveButton}
      {isBookingExistsError ? (
        <StationaryBookingExistsBanner existingBookingUrl={existingBookingUrl} />
      ) : (
        // Both modes: nothing else surfaces a create failure (no global mutation toast), and
        // toast mode showed nothing, leaving the customer retrying a dead confirm.
        createBookingError && <ApiErrorMessageWithTrans error={createBookingError} />
      )}
      {validationError && <FieldErrorMessage errorMessage={validationError} />}
      {refreshError && <FieldErrorMessage errorMessage={refreshError} />}
      <div className='flex w-full flex-col items-end gap-2 sm:flex-row sm:items-center sm:justify-end'>
        <NoddiButton variant='success' loading={isBusy} disabled={isDisabled} onClick={() => void handleClick()}>
          {ctaText}
        </NoddiButton>
      </div>
    </div>
  );
};
